
    const data = [
  {
    "title": "Principles",
    "id": "principles",
    "elements": [
      {
        "type": "paragraph",
        "text": "This framework is intended to help you systematically design digital infrastructure that meets a drug discovery/biotech organization's scientific requirements while allowing it to grow, evolve and integrate new technology such as AI/ML models."
      },
      {
        "type": "paragraph",
        "text": "The approach is based on three principles:"
      },
      {
        "type": "heading",
        "size": 2,
        "title": "1. Start from Workflows"
      },
      {
        "type": "paragraph",
        "text": "Digital tools and infrastructure don't exist in a vacuum. They need to fit into the team's processes and the flow of information that supports those processes. The design process should start with mapping out how the team works, identifying when and where they will interact with the system, then defining the interfaces that will best facilitate that interaction."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "2. Design for Change"
      },
      {
        "type": "paragraph",
        "text": "Drug discovery teams are constantly evolving as assays mature, drug programs move on to new stages and new technology is introduced. Digital infrastructure must be designed to support the team and its processes as they are today, while giving them the flexibility to grow and evolve. The best designs also encourage teams to adopt habits that will make the transition to new processes as smooth as possible."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "3. Don't Over (or Under) Engineer"
      },
      {
        "type": "paragraph",
        "text": "One of the hardest adjustments for software engineers and data architects from the tech world who move into drug discovery/biotech is adjusting to the nature of the technical complexity. Biological data has almost none of the volume and latency requirements that is central to engineering in the tech world. Instead, the complexity comes from tradeoffs between flexibility and consistency, and the relative chaos of biology."
      },
      {
        "type": "paragraph",
        "text": "It's still important not to under-engineer solutions, but recent entrants from the tech world are much more likely to over-engineer. This makes systems and workflows so brittle that they immediately break when processes inevitably change."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Outline"
      },
      {
        "type": "paragraph",
        "text": "The rest of the framework is split into three sections:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Workflows: Covers how to think about workflows at different levels of detail, and begin defining the technical requirements of the system."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Storage: Breaks down the static components related to storage into layers that each need to be addressed."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Compute: Provides a similar break down for the static components that support data processing, analysis, etc."
        ]
      }
    ]
  },
  {
    "title": "Workflows",
    "id": "workflows",
    "elements": [
      {
        "type": "heading",
        "size": 2,
        "title": "The Experiment Cycle"
      },
      {
        "type": "paragraph",
        "text": "Before you can design a digital platform, you need to understand what you're designing it for. While every drug discovery team has a unique set of assays, processes and requirements, most experiments can modeled as first setting up experimental conditions (Execution) then capturing measurements from those different conditions (Readout). The goal of the experiment is to understand how the different experimental conditions correlate with the different measurements."
      },
      {
        "type": "paragraph",
        "text": "To complete the framework, we just need to add a Planning step at the beginning, then Analysis and Decision steps at the end."
      },
      {
        "type": "paragraph",
        "text": "For each of these five steps, we'll focus on the information/data that needs to be captured:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Planning: Experiment planning spans everything from determining the high-level shape and objectives of an experiment to recording details such as plate maps, time points and concentrations. These details (the metadata) define the experimental conditions that will be needed in the Analysis step, so the teams that make these decisions must be deliberate and consistent about recording them."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Execution: While setting up the experimental conditions for samples in the lab, bench scientist may record operational details such as serial numbers of reagents, intermediate instrument readings and minor decisions that weren't addressed in Planning. All this metadata may seem trivial at the time, but it may prove necessary later for things like addressing batch effects and chasing down anomalies in the data."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Readout: Once the experimental conditions are created, the instruments that make the final measurements generate the readout data. While there are technical concerns related to getting the data off the instruments, capturing readout data is generally more straightforward than recording metadata during Planning or Execution."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Analysis: The analysis step starts with finding and collecting the metadata from Planning and Execution and the data from the Readout. This will be synthesized into a form that allows the readout measurements to be compared to the experimental conditions. The data is often refined through multiple processing steps, i.e. primary/secondary/tertiary analysis, each of which generates a smaller volume of more concentrated data, until the results are small enough to be interpreted by a human."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Decision: Finally, the analyzed information must be shared with stakeholders and team members to make a decision. The decision might be to do more experiments, to start writing an NDA, or something else. These decisions are often captured informally in emails, or just verbally. However, if you can capture them in a more structured form, they can be incredibly useful for orchestrating all the other steps."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Contexts"
      },
      {
        "type": "paragraph",
        "text": "The next step is to map out the different settings in which this work will happen, and how team members will want to interact in these contexts. This should include both how information flows between individuals and systems in each setting today and how the system should encourage information to flow."
      },
      {
        "type": "bullets",
        "bullets": [
          "Formal meetings: These often involve presentations. Consider how the data gets into the presentations, for example whether live data can be presented directly from an application or captured from the discussion. The structure and dynamics of meetings can also have a big impact on behavior in other contexts."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Informal discussions: These may be ad hoc meetings or impromptu conversations where team members want to quickly look up information and make quick decisions. Consider how you can you make it easy to find that data, then capture those decisions in a shared location."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Computer work: This may be individuals, pairs or small groups working together. This is the context that many design processes focus on."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Lab work: Bench scientists typically only have access to shared, dedicated lab computers when they're doing lab work, or may not have access at all. This can significantly limit metadata collection during Execution."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Async comms: A lot of a team's decisions are made via email, Slack, etc. and can be difficult to find and organize later. So you may want to consider how to move this decision making into other contexts, or at least make it easy for the team to capture this metadata in a shared system."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Interfaces"
      },
      {
        "type": "paragraph",
        "text": "In each of these contexts, team members will need to interact with different components of the system. Different interfaces will be appropriate in different contexts, with different kinds of users, for different kinds of interaction. Here are the most common types to consider:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Webapp: This has become the preferred interface for most mouse-based users. Direct access to shared resources. No need to install anything on each team member's laptop."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Desktop App: These have become less common, but haven't gone away completely, particularly for more specialized scientific tasks such as analyzing particular assays."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "API: Common for programmatic access to external services and resources, but can also be deployed internally. Usually accessed indirectly through a CLI, code or some other interface."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "CLI: Common for keyboard-based users who need to do recurring tasks. May interact with an API or run things locally."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Code: An increasingly common interface via code notebooks, but can also involve writing and running scripts in an IDE."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "MCP/LLM: This is becoming increasingly common for both mouse-based and keyboard-based users. Many MCP servers are just thin interfaces to an API. Either way, they can be run locally or provided as a shared internal or external service."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Configuration: Settings in desktop apps or shared internal or external services, users only interact with configuration on rare instances, but it's important to consider in your design."
        ]
      }
    ]
  },
  {
    "title": "Storage",
    "id": "storage",
    "elements": [
      {
        "type": "paragraph",
        "text": "There are many different ways to store data, depending on the type, volume and access requirements. In most cases, you won't need all the options covered in the different layers below, but it's useful to understand the differences to decide what you do need."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The \"Physical\" Layer"
      },
      {
        "type": "paragraph",
        "text": "The word \"physical\" is in quotes in the title because this layer isn't really physical storage in the old fashion sense of magnetic drives. There's usually a layer of virtualization that allows us to access the physical storage through the network. But because this virtual layer is thin, you usually won't get in trouble if you pretend the virtual and physical layers are the same. There are essentially three types of \"physical\" storage:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Local: The hard drive on a user's laptop/desktop/tablet/phone/etc"
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "On Prem: A (possibly virtual) hard drive in a server room that your organization owns, or maybe in an old desktop hiding under someone's desk."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Cloud: A (definitely virtual) hard drive in a server room owned by a Cloud provider that your organization rents (more or less)"
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The Organization Layer"
      },
      {
        "type": "paragraph",
        "text": "This layer determines whether data in the physical layer will be organized and accessed directly as files, indirectly as tables in a database, or in some other way. (Databases store the underlying data in files too, you just don't typically look at those files.)"
      },
      {
        "type": "paragraph",
        "text": "There are also questions about how the data will be organized into folders/tables/etc. but that topic that requires its own framework. This layer is mostly concerned with the type of software/database that defines the structure, and there are a handful of common options:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Files and folders: Give users or other systems direct access to the organizational structure of the physical/virtual layer. It's up to the external system or user to keep track of the internal structure of these files and decide what to do with them."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Relational database: This is a highly structured, classical database in which data is slotted into pre-defined tables with precise field types, managed references between records/tables and the ability to run complex queries, typically with SQL. This is useful when you know your data will fit a consistent shape and want to be able to leverage that consistency. It is somewhat limited in the volume it can handle, though modern relational databases like Postgres can scale to billions of records."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Query engines: This is a hybrid between files/folders and a relational database. The underlying data is managed directly as files with an internal structure like CSV or Parquet, then a query engine such as AWS Athena, Apache Spark or Databricks allows users to query them as if they were tables in a database. This provides more flexibility and scale than a relational database but references between records/tables are more YOLO. It's higher latency, which means that small queries take much longer than they would in a relational database, though large queries can be much faster."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Document database: Structurally, these databases are very similar to files and folders, in that each \"document\" can only be accessed as a single structured blob. But they typically also provide additional metadata fields that make it easier to organize and find documents, and they are faster at retrieving small documents compared to standard Cloud storage systems like AWS S3. Examples include DynamoDB, Apache CouchDB, or you can get a similar result with a JSONB column in a postgres database."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Graph database: Somewhere between a document and a relational database, graph databases generally allow more flexibility on individual fields than a relational database, while keeping strict references/relationships between records. They can be queried in ways that trace chains of these relationships, enabling graph/network analysis that isn't possible with other kinds of databases."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "External API: In some cases, you can (or have to) defer these decisions to someone else, and just access the data through an API that they provide."
        ]
      },
      {
        "type": "paragraph",
        "text": "There are also more specialized options for specific types of data such as TileDB, which basically acts like a query engine for large matrices (like NGS expression matrices). Sometimes, these start to blur the line between the organization layer and the control layer."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The Control Layer"
      },
      {
        "type": "paragraph",
        "text": "This layer controls how the system enforces your team's rules and conventions for how different kinds of data will be organized at the organization layer. It can also play a role in helping users find data later."
      },
      {
        "type": "paragraph",
        "text": "Defining the rules and conventions is a much more complex topic (with its own framework) and there's a wide ranges of options for enforcing them, but here are a few high-level categories. Your team may use different options for different types of data:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Manual: Let users determine where to put the data. There may be formally documented conventions that they're expected to follow, or it might be 100% their judgement, but either way the ultimate decision is the user's. This is the most common option for small biotech teams."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Data Catalog: Use software to translate the scientific context of a dataset into technical context, i.e. the folder or table that the data should be stored in. The data catalog may put the data in that location for the user, or just tell them where to put it themselves. (This starts to bleed into the Access layer.)"
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Automation: For this option, the user is taken out of the equation and a script translates the scientific context into technical context, then copies the data to where it goes. This is a common option for collecting readout data, and handling high-volume, highly consistent assays."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The Access Layer"
      },
      {
        "type": "paragraph",
        "text": "This layer is tightly coupled to the Control layer, but separate enough to deserve its own consideration. The Access layer determines how users will write and access data once they've determined where it goes or where it is. Here are a few categories of options to consider, though this list isn't exhaustive:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Fully abstracted: In some cases, users can access data directly through analysis software or some other kind of immersive platform. For example, they may use Spotfire to query a shared database."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Mounted drive: For a file/folder Organization layer, remote storage such as S3 buckets can often be mounted to users' laptops/desktops so that they can access them as if they were files on their hard drive."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Upload/download: In some cases, you may want users to download a local copy of any data, save new data on their local hard drive, then upload it once it's complete."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "API: You may want users to access database data directly through the database's API, particularly for relational databases. They may access this API via a desktop app, a CLI or something else, as discussed in the Interfaces section."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The Discovery Layer"
      },
      {
        "type": "paragraph",
        "text": "The Control and Access layers help users who know the exact data they want. But when building for high-context users, it's easy to forget about new team members, peripheral teams and team leaders who don't have that context. This is the hardest layer to get right, but here are two options to think about:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Data Catalog: This was covered in the Control layer, but here the catalog serves a different purpose. At the Control layer, a catalog helps users find data that they know exists. At the Discovery layer, its goal is to help users get a high-level overview of all the data, then zoom in to what they're looking for. This is different enough that it could justify a separate data catalog for data discovery."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Documentation: A simpler (though not necessarily easier) option is to carefully document data conventions and the state of the system from the perspective of an outsider. This may include regularly generated reports pulled from the system's metadata."
        ]
      },
      {
        "type": "paragraph",
        "text": "Again, this layer is the hardest to get right, and is missing from many systems/platforms. It's typically only worth working on after the rest of the layers are solid."
      }
    ]
  },
  {
    "title": "Compute",
    "id": "compute",
    "elements": [
      {
        "type": "paragraph",
        "text": "How you process and analyze data is an overall simpler problem than how you store it, but there are still plenty of options to consider. Again, we'll break it down into a number of layers that aren't independent from each other, but require separate consideration."
      },
      {
        "type": "heading",
        "size": 2,
        "title": "The \"Physical\" Layer"
      },
      {
        "type": "paragraph",
        "text": "As with data storage, we'll use the term \"physical\" to mean a combination of the physical hardware and the virtualization that comes with Cloud/data centers. The options are almost the same as with storage, but with two additional options that have more to do with pricing than architecture:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Local: The CPU and/or GPU on a user's laptop/desktop/tablet/phone/etc."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "On Prem: One or more (potentially virtual) CPUs/GPUs in a server room that your organization owns"
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Cloud: One or more (definitely virtual) CPUs/GPUs in a server room owned by a Cloud provider that your organization rents (more or less). You pay based on how long you rent them for."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Serverless: Despite the name, serverless architecture still uses CPUs/GPUs in a server, but you pay based on the amount of processing they do, rather than you deciding how long you rent them for."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Token/usage-based: Similar to serverless, but specifically for LLMs and at a much finer grain of detail. A token is roughly a word that an LLM reads or writes."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Provisioning"
      },
      {
        "type": "paragraph",
        "text": "This layer defines how you decide which CPUs/GPUs/servers are available to which systems or users. It's mostly applicable when using On Prem or Cloud for the Physical layer and some common options include:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Direct control: Give users complete control over what kind of compute they need, such as the amount of memory, the number of GPUs, etc. They may access it directly from the Cloud console or CLI or via a custom UI or CLI, but they have as much flexibility as the Cloud provider or data center allows."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Limited control: Users select the compute they need via an interface that gives them a narrowed-down list of options to select from. The options may be presented in terms of technical details or translated into a more abstract form."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Fully automated/managed: The decision for what kind of resources to use is determined entirely by the type of workflow that's going to be run, whether it's triggered by automation or by a user."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Orchestration"
      },
      {
        "type": "paragraph",
        "text": "The Orchestration layer determines what code gets run and where in the Physical layer it runs. There are essentially two kinds of options for this:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Single-processor Workflow: Everything runs on a single machine in the Physical/Virtual layer. It could be a single program or a series of programs triggered by a Bash script or something else. It could be running on your laptop or a Cloud VM. The defining feature is that the entire process is handled by that single processor."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Distributed Pipeline: In this case, the overall process is split into smaller steps that are distributed between different machines, coordinated by another machine that may also handle some of the individual steps. While this can theoretically be done with Bash scripts, it's mostly handled by specialized distributed computing software/frameworks that interact with both the Provisioning layer and the Physical layer."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Long-running Server: This is a program that runs indefinitely. This is the model used by web servers, APIs and other resources that need to be available on demand. In this case, the orchestration layer is responsible for making sure the server keeps running by restarting it if it ever goes down."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Scheduling"
      },
      {
        "type": "paragraph",
        "text": "The Scheduling layer determines when processes run. There are essentially three options, depending on what's available in the previous three layers:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Manual: Processes are triggered by users, often with custom parameters defined by whatever context they're in. They may trigger it through different kinds of interfaces, but that's covered by the Interfaces framework."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Scheduled: Processes run at pre-determined times."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Triggered: Processes are run based on external triggers such as a file being dropped into a particular directory."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Development"
      },
      {
        "type": "paragraph",
        "text": "This layer determines the logic of the processes that are run by the Orchestration layer. In the past, I would've treated this as a sort of meta-process outside this framework. But the line between developing and running code is increasingly blurring, so I think it needs to be treated as another layer. Here are the basic categories:"
      },
      {
        "type": "bullets",
        "bullets": [
          "Programs/scripts: This is how programs were traditionally defined, as code files that get \"compiled\" into programs and run as a single block. This still happens today, but it's no longer the only way."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Code Notebooks: The canonical example is a Jupyter notebook, but there are a growing number of alternatives. Notebooks can be run locally or on a remote/Cloud server, and even trigger processes/workflows on other machines. Completed notebooks can also be run \"headless\" as scripts that don't require user interaction."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "Low/no-code Platforms: These are interfaces that allow users to piece together steps in a pipeline by dragging arrows between boxes and filling out forms, sometimes using a bit of code for customized logic. They were more popular before vibe coding, but they still exist."
        ]
      },
      {
        "type": "bullets",
        "bullets": [
          "LLM/Agent Workflows: Sure, LLMs can write programs/scripts that get used repeatedly. But they can also write one-off scripts and trigger processes that carry out custom workflows based on natural language instructions from users. So the natural language instructions effectively become scripts in their own right, with the LLM as the compiler/interpreter."
        ]
      },
      {
        "type": "heading",
        "size": 2,
        "title": "Version Control & Deployment"
      },
      {
        "type": "paragraph",
        "text": "The Version Control and Deployment layers are starting to get into the weeds of software engineering/DevOps and there aren't really different categories of options as much as well established best practices with different options for how you implement those best practices."
      },
      {
        "type": "paragraph",
        "text": "It is important to adjust those best practices based on the level of flexibility vs reliability you need for each process. So it's absolutely essential that you address these layers in your system design, but it isn't as simple as picking from a short list of options."
      }
    ]
  }
]

    export default data;
    